import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import earthTexture from '../../../assets/earth_black.png';

const Globe = () => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);

  useEffect(() => {
    if (!sceneRef.current) {
      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      // Create scene with white background.
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xffffff);
      sceneRef.current = scene;

      // Camera.
      const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
      camera.position.set(0.0, 1.5, 3.0);
      cameraRef.current = camera;

      // Renderer.
      const renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
      });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      mountRef.current.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      // OrbitControls.
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.autoRotate = false;
      controls.enablePan = false;
      controls.enableZoom = false;

      // Lights.
      const ambient = new THREE.AmbientLight(0xffffff, 0.5);
      scene.add(ambient);
      const directional = new THREE.DirectionalLight(0xffffff, 0.5);
      directional.position.set(5.0, 2.0, 5.0).normalize();
      scene.add(directional);

      // Load the earth texture.
      const textureLoader = new THREE.TextureLoader();
      const earthMap = textureLoader.load(earthTexture);

      // Use an Icosahedron geometry for a more uniform distribution.
      const geometry = new THREE.IcosahedronGeometry(1.2, 35);

      // Shader material for the earth points.
      const material = new THREE.ShaderMaterial({
        uniforms: {
          uTexture: { value: earthMap },
          uPointColor: { value: new THREE.Color('#493fff') },
        },
        vertexShader: `
          varying vec2 vUv;
          varying float vFade;
          void main() {
            vUv = uv;
            vec4 viewPos = modelViewMatrix * vec4(position, 1.0);
            vFade = clamp((viewPos.z + 1.0) / -4.0, 0.0, 1.0);
            gl_Position = projectionMatrix * viewPos;
            gl_PointSize = 5.0;
          }
        `,
        fragmentShader: `
          varying vec2 vUv;
          varying float vFade;
          uniform sampler2D uTexture;
          uniform vec3 uPointColor;
          void main() {
            vec4 texColor = texture2D(uTexture, vUv);
            if(texColor.r > 0.5) {
              discard;
            }
            vec2 center = gl_PointCoord - vec2(0.5);
            if (length(center) > 0.5) {
              discard;
            }
            vec3 finalColor = mix(uPointColor, vec3(1.0), vFade);
            finalColor = min(finalColor * 0.85, vec3(1.0));
            gl_FragColor = vec4(finalColor, 1.0);
          }
        `,
        transparent: true,
      });

      // Create the earth points object.
      const earthPoints = new THREE.Points(geometry, material);
      scene.add(earthPoints);

      const radius = 1.2;
      const offset = 0.02;

      const createTextMesh = (text, lat, lon, options = {}) => {
        // Create a canvas and prepare to draw the text with a pill background
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        // Set the font before measuring text (smaller font)
        const fontSize = 16;
        context.font = `${fontSize}px monospace`;

        // Measure the text width
        const textMetrics = context.measureText(text);
        const textWidth = textMetrics.width;
        const textHeight = fontSize; // Approximate text height based on font size

        // Define padding for the pill background (smaller padding)
        const padding = 4;
        const pillWidth = textWidth + 2 * padding;
        const pillHeight = textHeight + 2 * padding;

        // Set canvas dimensions to match the pill size scaled by devicePixelRatio
        const scaleFactor = window.devicePixelRatio || 1;
        canvas.width = pillWidth * scaleFactor;
        canvas.height = pillHeight * scaleFactor;
        // Scale the drawing context so that drawing commands use the original pill dimensions
        context.scale(scaleFactor, scaleFactor);

        // Reset font settings after resizing the canvas
        context.font = `${fontSize}px monospace`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        const fontColor = options.fontColor || 'black';

        // Draw the pill background: a light grey rounded rectangle
        const pillRadius = pillHeight / 2; // For a pill shape, use half the height as the radius
        context.fillStyle = '#ECEDF5'; // Light grey color
        context.beginPath();
        context.moveTo(pillRadius, 0);
        context.lineTo(pillWidth - pillRadius, 0);
        context.quadraticCurveTo(pillWidth, 0, pillWidth, pillRadius);
        context.lineTo(pillWidth, pillHeight - pillRadius);
        context.quadraticCurveTo(pillWidth, pillHeight, pillWidth - pillRadius, pillHeight);
        context.lineTo(pillRadius, pillHeight);
        context.quadraticCurveTo(0, pillHeight, 0, pillHeight - pillRadius);
        context.lineTo(0, pillRadius);
        context.quadraticCurveTo(0, 0, pillRadius, 0);
        context.closePath();
        context.fill();

        // Draw the text on top of the pill background with the specified color
        context.fillStyle = fontColor;
        context.fillText(text, pillWidth / 2, pillHeight / 2);

        // Create a texture from the canvas
        const textTexture = new THREE.CanvasTexture(canvas);
        textTexture.minFilter = THREE.LinearFilter;
        textTexture.needsUpdate = true;

        // Create a plane geometry for the text that maintains the correct aspect ratio
        // Use a fixed geometry height and compute width accordingly
        const geoHeight = 0.09;
        const geoWidth = geoHeight * (pillWidth / pillHeight);
        const textGeometry = new THREE.PlaneGeometry(geoWidth, geoHeight);
        const textMaterial = new THREE.MeshBasicMaterial({
          map: textTexture,
          transparent: true,
          side: THREE.DoubleSide,
        });
        const textMesh = new THREE.Mesh(textGeometry, textMaterial);
        textMesh.renderOrder = 1;

        // Compute the position on the globe based on latitude and longitude
        const phi = ((90 - lat) * Math.PI) / 180;
        const theta = (lon * Math.PI) / 180;
        const rOffset = radius + offset;
        textMesh.position.set(
          rOffset * Math.sin(phi) * Math.cos(theta),
          rOffset * Math.cos(phi),
          rOffset * Math.sin(phi) * Math.sin(theta),
        );

        // Orient the text to face outward using the globe's normal
        const normal = new THREE.Vector3(
          Math.sin(phi) * Math.cos(theta),
          Math.cos(phi),
          Math.sin(phi) * Math.sin(theta),
        );
        textMesh.lookAt(textMesh.position.clone().add(normal));
        return textMesh;
      };

      // Add text meshes to the globe
      const textMeshUK = createTextMesh('🇬🇧 Report due 4 April', 490.0, -1.0);
      earthPoints.add(textMeshUK);

      const textMeshAU = createTextMesh('🇦🇺 Report overdue', -25.0, 46.0, {
        fontColor: 'red',
      });
      earthPoints.add(textMeshAU);

      const textMeshUS = createTextMesh('🇺🇸 Upcoming report', 40, -80);
      earthPoints.add(textMeshUS);

      const textMeshes = [textMeshUK, textMeshAU, textMeshUS];

      // Handle window resize.
      const handleResize = () => {
        const w = mountRef.current.clientWidth;
        const h = mountRef.current.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      window.addEventListener('resize', handleResize);

      // Scroll effect (unchanged).
      const onScroll = () => {
        const targetSection = document.getElementById('builtby');
        if (!targetSection) return;
        const rect = targetSection.getBoundingClientRect();
        const inView = rect.top < window.innerHeight * 0.7;
        if (inView) {
          camera.position.set(0.2, 1.2, 2.2);
        } else {
          camera.position.set(0.0, 1.5, 3.0);
        }
      };
      window.addEventListener('scroll', onScroll);

      // Animation loop.
      const animate = () => {
        requestAnimationFrame(animate);
        // Rotate the globe; since the text is attached as a child, it rotates with it.
        earthPoints.rotation.y += 0.002;

        // Update opacity for each text mesh based on its position relative to the globe center.
        // Compute the vector from the globe center (assumed at (0,0,0)) to the camera and normalize it.
        const camVector = camera.position.clone().normalize();
        textMeshes.forEach((mesh) => {
          const meshWorldPos = new THREE.Vector3();
          mesh.getWorldPosition(meshWorldPos);
          // Normalize the mesh's world position to get its direction from the center.
          const meshDir = meshWorldPos.clone().normalize();
          // Dot product: 1 means fully facing the camera, -1 means fully away.
          const dot = meshDir.dot(camVector);
          // Clamp dot product to [0,1] for opacity.
          const fade = THREE.MathUtils.clamp(dot, 0, 1);
          mesh.material.opacity = fade;
        });

        renderer.render(scene, camera);
      };
      animate();

      // Cleanup on unmount.
      return () => {
        if (mountRef.current && renderer.domElement) {
          mountRef.current.removeChild(renderer.domElement);
        }
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', onScroll);
      };
    }
  }, []);

  return <div ref={mountRef} style={{ width: '100%', height: '100%' }} />;
};

export default Globe;
